import type {ReactElement} from 'react';
import React, { useEffect} from 'react';
import {areThereNoBrandsToShow} from '@Libraries/brands-library';
import {setGridItems} from '@Components/base-grid';
import type { GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {GridUserImageItemStorage} from '@Components/base-grid/components/grid-user-image-item';
import MasonryGrid from '@Components/masonry-grid';
import type {BrandContent, BrandLogo} from '@Components/editor-brands/editor-brands.types';
import {useAppDispatch, useAppSelector} from '@/hooks';

export interface BrandLogosProps {
  gridId: string;
  selectionGroupId: string;
  maxSelectionNumber?: number;

  onItemClicked(item: GridItemStorage, isDoubleClick: boolean): void;
}

function BrandLogos({...props}: BrandLogosProps): ReactElement | null {
  const brands = useAppSelector((state) => {
    return state.brands.brands;
  });
  const activeBrandId: string = useAppSelector((state) => {
    return state.brands.activeBrandId ?? '';
  });
  const brandsLoadingState = useAppSelector((state) => {
    return state.brands.brandsLoadingState;
  });
  const brandContent: Record<string, BrandContent> = useAppSelector((state) => {
    return state.brands.brandContent;
  });
  const brandLogos: Record<string, BrandLogo> = useAppSelector((state) => {
    return state.brands.logos;
  });
  const brandLogoIds = areThereNoBrandsToShow(brands, brandsLoadingState) ? null : brandContent[activeBrandId].logos.ids;
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(
      setGridItems({
        id: props.gridId,
        gridSelectionGroupId: props.selectionGroupId,
        items: getItems(),
      })
    );
  }, [brandLogoIds]);

  const getItems = (): Array<GridUserImageItemStorage> => {
    if (!Object.keys(brandContent).length) {
      return [];
    }
    const assets = brandContent[activeBrandId];
    const logos = [];
    for (const id of assets.logos.ids) {
      const logo = brandLogos[id];
      logos.push({
        type: GRID_ITEM_TYPE.USER_IMAGE,
        id: logo.id.toString(),
        hashedFilename: logo.filename,
        extension: logo.ext,
        uploaderName: logo.uploaderName,
        uploaderId: parseInt(logo.uploaderId, 10),
        hasTransparency: logo.hasTransparency,
        source: logo.src,
        isHidden: false,
        width: logo.width,
        height: logo.height,
      } as GridUserImageItemStorage);
    }
    return logos;
  };

  const onItemClicked = (item: GridItemStorage, isDoubleClick: boolean): void => {
    props.onItemClicked(item, isDoubleClick);
  };

  return (
    <MasonryGrid
      className="_full-width spacing-p-t-2 spacing-p-b-6"
      gridId={props.gridId}
      gridSelectionGroupId={props.selectionGroupId}
      customOnGridItemClicked={onItemClicked}
      maxSelectionNumber={props.maxSelectionNumber}
      makeContentScrollable={false}
    />
  );
}

export default BrandLogos;
